import { useCallback, useEffect, useMemo, useState } from "react";

import type { DateValueType } from "../../DatepickerV2";
import type { DropdownOption } from "../../DropDownV2";
import type {
  LeadsPageAnalytics,
  ReturnedTranslatedContentLeadPageHook,
} from "../types";
import type { LeadTableFilterSubPropsV2 } from "./types";

import { useIsMobile } from "../../hooks";
import { formatToDateString } from "../../utils/date";
import { useDebounceFn } from "../../utils/debounce";
import { PII_FIELDS } from "../consts";
import { useLeadsContextV2 } from "../providers/LeadsProviderV2";
import { DesktopFiltersV2 } from "./DesktopFiltersV2";
import { MobileFiltersV2 } from "./MobileFiltersV2";

type LeadFiltersV2Props = {
  analytics?: LeadsPageAnalytics;
  translations: ReturnedTranslatedContentLeadPageHook;
  isBackoffice?: boolean;
};

export const LeadFiltersV2 = ({
  analytics,
  translations,
  isBackoffice,
}: LeadFiltersV2Props) => {
  const {
    state: { searchQuery, dateRange },
    resources: {
      table: { data: tableData },
      filters,
    },
    actions,
    runResource,
  } = useLeadsContextV2();

  const [initialCountryCodes, setInitialCountryCodes] = useState<
    string[] | undefined
  >(undefined);

  useEffect(() => {
    if (initialCountryCodes === undefined && filters.status === "success") {
      setInitialCountryCodes(filters.data?.countryCodes ?? []);
    }
  }, [filters.status, filters.data, initialCountryCodes]);
  const isMobile = useIsMobile();

  const canViewPII = !PII_FIELDS.every((field) =>
    tableData?.redactedFields?.includes(field),
  );

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchEvent(value);
  }, 1000);

  const handleDateRangePicker = useCallback(
    (value: DateValueType) => {
      if (
        formatToDateString(value?.startDate) ===
          formatToDateString(dateRange.from) ||
        formatToDateString(value?.endDate) === formatToDateString(dateRange.to)
      ) {
        analytics?.trackDateRangeChangeEvent(
          formatToDateString(value?.startDate),
          formatToDateString(value?.endDate),
        );
      }
      actions.setDateRange({
        from: value?.startDate ?? null,
        to: value?.endDate ?? null,
      });
    },
    [actions, analytics, dateRange.from, dateRange.to],
  );

  const handleCountryDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newSelectedCountryCodes = options.map((o) => o.value);
      actions.setSelected("countryCodes", newSelectedCountryCodes);
    },
    [actions],
  );

  const handleExportButton = useCallback(() => {
    runResource("csv");
    analytics?.trackTableExportCSVEvent({
      searchQuery,
      fromDate: formatToDateString(dateRange.from),
      toDate: formatToDateString(dateRange.to),
    });
  }, [runResource, dateRange.from, dateRange.to, searchQuery, analytics]);

  const filterConfig = useMemo<LeadTableFilterSubPropsV2>(
    () => ({
      handleDateRangePicker,
      handleExportButton,
      handleCountryDropdown,
      handleSearchInput,
      canViewPII,
      redactedFields: tableData?.redactedFields || [],
      translations,
      isBackoffice,
      initialCountryCodes,
    }),
    [
      handleDateRangePicker,
      handleExportButton,
      handleCountryDropdown,
      handleSearchInput,
      canViewPII,
      tableData?.redactedFields,
      translations,
      isBackoffice,
      initialCountryCodes,
    ],
  );

  return isMobile ? (
    <MobileFiltersV2 {...filterConfig} />
  ) : (
    <DesktopFiltersV2 {...filterConfig} />
  );
};
